import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Feedback';
import { PageHeader } from '@/app/PlaceholderPage';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  PELITA_ALIASES,
  PELITA_TEMPLATE,
  TBM_ALIASES,
  TBM_TEMPLATE,
  mapHeaders,
  parseCsv,
} from './csv';

type Kind = 'tbm' | 'individuals';

interface Candidate {
  id: string;
  name: string;
  path: string;
}

interface Issue {
  field: string;
  message: string;
  candidates?: Candidate[];
}

interface PreviewRow {
  rowNumber: number;
  severity: 'ready' | 'warning' | 'rejected';
  issues: Issue[];
  regionId?: string | null;
  tbmId?: string | null;
  [key: string]: unknown;
}

interface PreviewResult {
  rows: PreviewRow[];
  summary: { ready: number; warning: number; rejected: number };
}

const CONFIG = {
  tbm: {
    title: 'Impor Data TBM',
    template: TBM_TEMPLATE,
    templateName: 'templat-tbm.csv',
    aliases: TBM_ALIASES,
    backTo: '/database/tbm',
    resolveField: 'regionId' as const,
    /** TBM tanpa desa tidak boleh masuk — agregasi wilayah bergantung padanya. */
    resolveRequired: true,
    label: 'TBM',
  },
  individuals: {
    title: 'Impor Data PELITA SLI',
    template: PELITA_TEMPLATE,
    templateName: 'templat-pelita.csv',
    aliases: PELITA_ALIASES,
    backTo: '/database/pelita',
    resolveField: 'tbmId' as const,
    /**
     * PELITA BOLEH masuk tanpa TBM, lalu dipasangkan lewat form. Menolak
     * seluruh data orangnya hanya karena nama TBM salah ketik jauh lebih
     * merugikan daripada membiarkannya menunggu sebentar tanpa TBM.
     */
    resolveRequired: false,
    label: 'PELITA SLI',
  },
};

/**
 * L-05d — Wizard impor 3 langkah.
 *
 * Nilai utamanya ada di langkah 2: memvalidasi dan MENCOCOKKAN WILAYAH sebelum
 * apa pun ditulis ke database. Tanpa itu, satu impor bisa menciptakan puluhan
 * wilayah kembar yang memecah agregasi dashboard.
 */
export function ImportWizard({ kind }: { kind: Kind }) {
  const config = CONFIG[kind];
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [overrides, setOverrides] = useState<Record<number, string>>({});
  const [conflictMode, setConflictMode] = useState<'skip' | 'update'>('skip');
  const [banner, setBanner] = useState<string | null>(null);
  const [done, setDone] = useState<{ created: number; updated: number; skipped: number } | null>(
    null,
  );

  const previewMutation = useMutation({
    mutationFn: (rows: unknown[]) => api.post<PreviewResult>(`/import/${kind}/preview`, { rows }),
    onSuccess: (res) => {
      setPreview(res);
      setBanner(null);
    },
    onError: (err) =>
      setBanner(err instanceof ApiError ? err.message : 'Gagal memvalidasi berkas.'),
  });

  const commitMutation = useMutation({
    mutationFn: (rows: unknown[]) =>
      api.post<{ created: number; updated: number; skipped: number }>(`/import/${kind}/commit`, {
        rows,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries();
      setDone(res);
    },
    // Sengaja TIDAK memakai setBanner: banner ada di puncak halaman, sedangkan
    // tombol Impor ada di dasar daftar yang panjang. Galatnya ditampilkan
    // langsung di bawah tombol — lihat commitMutation.isError di langkah 3.
  });

  async function onFile(file: File) {
    setFileName(file.name);
    setPreview(null);
    setOverrides({});
    setDone(null);

    const text = await file.text();
    const grid = parseCsv(text);

    if (grid.length < 2) {
      setBanner('Berkas kosong atau hanya berisi baris judul.');
      return;
    }

    const index = mapHeaders(grid[0]!, config.aliases);
    if (Object.keys(index).length === 0) {
      setBanner('Judul kolom tidak dikenali. Unduh templat di langkah 1 sebagai acuan.');
      return;
    }

    const rows = grid.slice(1).map((cells, i) => {
      const row: Record<string, unknown> = { rowNumber: i + 2 };
      for (const [field, col] of Object.entries(index)) row[field] = cells[col] ?? '';
      return row;
    });

    previewMutation.mutate(rows);
  }

  function downloadTemplate() {
    const blob = new Blob(['\uFEFF' + config.template], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = config.templateName;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Baris siap simpan = tidak ditolak, dan acuannya sudah terisi bila wajib. */
  function collectImportable() {
    if (!preview) return [];
    const rows = preview.rows
      .filter((r) => r.severity !== 'rejected')
      .map((r) => ({
        ...r,
        [config.resolveField]: overrides[r.rowNumber] ?? r[config.resolveField] ?? null,
      }));

    return config.resolveRequired ? rows.filter((r) => Boolean(r[config.resolveField])) : rows;
  }

  const importable = collectImportable();

  // "Belum dicocokkan" = baris yang tidak ditolak tapi acuannya masih kosong.
  // Sebelumnya dihitung dari SEMUA baris berstatus warning, sehingga baris
  // yang peringatannya soal nama ganda — desanya sudah ketemu, sudah siap
  // diimpor — ikut dilaporkan "akan dilewati" padahal tidak.
  const unresolved =
    preview && config.resolveRequired
      ? preview.rows.filter(
          (r) => r.severity !== 'rejected' && !(overrides[r.rowNumber] ?? r[config.resolveField]),
        ).length
      : 0;

  if (done) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title={config.title} />
        <Card>
          <div className="flex flex-col items-center py-8 text-center">
            <span className="mb-3 text-4xl" aria-hidden>
              ✅
            </span>
            <h2 className="text-xl font-semibold text-ink">Impor selesai</h2>
            <p className="mt-2 text-ink-secondary">
              {done.created} data baru ditambahkan
              {done.updated > 0 && `, ${done.updated} diperbarui`}
              {done.skipped > 0 && `, ${done.skipped} dilewati`}.
            </p>
            <div className="mt-6 flex gap-3">
              <Button onClick={() => navigate(config.backTo)}>Lihat Data</Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setDone(null);
                  setPreview(null);
                  setFileName(null);
                }}
              >
                Impor Lagi
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Link to={config.backTo} className="text-sm text-ink-secondary hover:text-primary">
        ← Kembali
      </Link>

      <PageHeader
        title={config.title}
        description="Tiga langkah: unduh templat, unggah berkas, lalu periksa sebelum disimpan."
      />

      {banner && <Banner tone="danger">{banner}</Banner>}

      <Card>
        <Step n={1} title="Unduh templat" done={Boolean(fileName)}>
          <p className="mb-3 text-ink-secondary">
            Susun data mengikuti kolom pada templat. Judul kolom yang mirip tetap dikenali —
            misalnya “Kabupaten” untuk kolom Wilayah.
          </p>
          <Button variant="secondary" onClick={downloadTemplate}>
            ⭳ {config.templateName}
          </Button>
        </Step>
      </Card>

      <Card>
        <Step n={2} title="Unggah berkas CSV" done={Boolean(preview)}>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[var(--radius-input)] border-2 border-dashed border-border px-6 py-10 text-center hover:border-primary hover:bg-primary-soft/40">
            <span className="text-2xl" aria-hidden>
              📎
            </span>
            <span className="font-medium text-ink">
              {fileName ?? 'Pilih berkas CSV'}
            </span>
            <span className="text-[13px] text-ink-muted">
              Ekspor dari Excel atau Google Sheets sebagai CSV
            </span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onFile(file);
              }}
            />
          </label>
          {previewMutation.isPending && (
            <p className="mt-3 text-sm text-ink-secondary">Memvalidasi…</p>
          )}
        </Step>
      </Card>

      {preview && (
        <Card>
          <Step n={3} title="Periksa & perbaiki" done={false}>
            <div className="mb-4 flex flex-wrap gap-2.5">
              <Tally tone="success" count={preview.summary.ready} label="siap diimpor" />
              <Tally tone="warn" count={preview.summary.warning} label="perlu perhatian" />
              <Tally tone="danger" count={preview.summary.rejected} label="ditolak" />
            </div>

            <ul className="flex flex-col gap-2.5">
              {preview.rows
                .filter((r) => r.severity !== 'ready')
                .map((row) => (
                  <RowIssueCard
                    key={row.rowNumber}
                    row={row}
                    override={overrides[row.rowNumber]}
                    onResolve={(id) =>
                      setOverrides((o) => ({ ...o, [row.rowNumber]: id }))
                    }
                  />
                ))}
            </ul>

            {preview.summary.ready > 0 && preview.rows.every((r) => r.severity === 'ready') && (
              <p className="text-ink-secondary">
                Semua baris lolos pemeriksaan. Tidak ada yang perlu diperbaiki.
              </p>
            )}

            {preview.rows.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-sm font-medium text-ink">Pratinjau Hasil Pembacaan Berkas:</p>
                <div className="overflow-x-auto rounded-[var(--radius-input)] border border-border">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-border bg-app/60 text-[13px] text-ink-secondary">
                        <th className="px-3 py-2 font-medium">Baris</th>
                        <th className="px-3 py-2 font-medium">Nama {kind === 'tbm' ? 'TBM' : 'PELITA SLI'}</th>
                        <th className="px-3 py-2 font-medium">{kind === 'tbm' ? 'Wilayah / Desa' : 'TBM Terkait'}</th>
                        {kind === 'tbm' && <th className="px-3 py-2 font-medium">Angkatan</th>}
                        {kind === 'tbm' && <th className="px-3 py-2 font-medium">Bergabung</th>}
                        {kind === 'individuals' && <th className="px-3 py-2 font-medium">Profesi</th>}
                        {kind === 'individuals' && <th className="px-3 py-2 font-medium">Pendidikan</th>}
                        <th className="px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {preview.rows.slice(0, 10).map((r) => (
                        <tr key={r.rowNumber} className="hover:bg-app/40">
                          <td className="px-3 py-2 tabular text-ink-muted">{r.rowNumber}</td>
                          <td className="px-3 py-2 font-medium text-ink">{String(r.name || '—')}</td>
                          <td className="px-3 py-2 text-ink-secondary">
                            {kind === 'tbm' ? String(r.region || '—') : String(r.tbmName || '—')}
                          </td>
                          {kind === 'tbm' && (
                            <td className="px-3 py-2 tabular text-ink-secondary">{String(r.angkatan || '—')}</td>
                          )}
                          {kind === 'tbm' && (
                            <td className="px-3 py-2 tabular text-ink-secondary">{String(r.joinedPeriod || '—')}</td>
                          )}
                          {kind === 'individuals' && (
                            <td className="px-3 py-2 text-ink-secondary">{String(r.profession || '—')}</td>
                          )}
                          {kind === 'individuals' && (
                            <td className="px-3 py-2 tabular text-ink-secondary">{String(r.lastEducation || '—')}</td>
                          )}
                          <td className="px-3 py-2">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                r.severity === 'ready' && 'bg-success-soft text-success',
                                r.severity === 'warning' && 'bg-warn-soft text-[#9a6400]',
                                r.severity === 'rejected' && 'bg-danger-soft text-danger',
                              )}
                            >
                              {r.severity === 'ready' ? '✓ Siap' : r.severity === 'warning' ? '⚠ Perlu dicek' : '✗ Ditolak'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.rows.length > 10 && (
                    <p className="p-2 text-center text-xs text-ink-muted bg-app/30 border-t border-border">
                      Menampilkan 10 baris pertama dari {preview.rows.length} total baris
                    </p>
                  )}
                </div>
              </div>
            )}

            {kind === 'tbm' && (
              <div className="mt-5">
                <p className="mb-2 text-sm font-medium text-ink">Kalau nama TBM sudah ada:</p>
                <div className="flex gap-2">
                  {(['skip', 'update'] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setConflictMode(mode)}
                      className={cn(
                        'min-h-10 rounded-full border px-4 text-sm font-medium',
                        conflictMode === mode
                          ? 'border-primary bg-primary-soft text-primary'
                          : 'border-border text-ink-secondary',
                      )}
                    >
                      {mode === 'skip' ? 'Lewati' : 'Perbarui datanya'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button
                onClick={() =>
                  commitMutation.mutate(
                    importable.map((r) => ({ ...r, onConflict: conflictMode })),
                  )
                }
                loading={commitMutation.isPending}
                disabled={importable.length === 0}
              >
                Impor {importable.length} baris
              </Button>
              {unresolved > 0 && (
                <p className="text-[13px] text-[#9a6400]">
                  {unresolved} baris belum dicocokkan dan akan dilewati.
                </p>
              )}
              {importable.length === 0 && (
                <p className="text-[13px] text-ink-secondary">
                  Belum ada baris yang siap — cocokkan wilayahnya dulu di daftar di atas.
                </p>
              )}
            </div>

            {commitMutation.isError && (
              <div className="mt-4">
                <Banner tone="danger">
                  {commitMutation.error instanceof ApiError
                    ? commitMutation.error.message
                    : 'Gagal menyimpan data.'}
                </Banner>
              </div>
            )}
          </Step>
        </Card>
      )}
    </div>
  );
}

function Step({
  n,
  title,
  done,
  children,
}: {
  n: number;
  title: string;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2.5">
        <span
          className={cn(
            'grid size-7 shrink-0 place-items-center rounded-full text-sm font-semibold',
            done ? 'bg-success text-white' : 'bg-primary-soft text-primary',
          )}
        >
          {done ? '✓' : n}
        </span>
        <h2 className="text-[17px] font-semibold text-ink">{title}</h2>
      </div>
      <div className="pl-[38px]">{children}</div>
    </div>
  );
}

function Tally({
  tone,
  count,
  label,
}: {
  tone: 'success' | 'warn' | 'danger';
  count: number;
  label: string;
}) {
  const tones = {
    success: 'bg-success-soft text-success',
    warn: 'bg-warn-soft text-[#9a6400]',
    danger: 'bg-danger-soft text-danger',
  };
  const marks = { success: '✓', warn: '⚠', danger: '✗' };

  return (
    <span className={cn('rounded-full px-3.5 py-1.5 text-sm font-medium', tones[tone])}>
      {marks[tone]} {count} {label}
    </span>
  );
}

function RowIssueCard({
  row,
  override,
  onResolve,
}: {
  row: PreviewRow;
  override?: string;
  onResolve: (id: string) => void;
}) {
  const rejected = row.severity === 'rejected';

  return (
    <li
      className={cn(
        'rounded-[var(--radius-input)] border p-3.5',
        rejected ? 'border-danger/30 bg-danger-soft/40' : 'border-warn/30 bg-warn-soft/40',
      )}
    >
      <p className="text-sm font-medium text-ink">
        Baris {row.rowNumber}
        {typeof row.name === 'string' && row.name ? ` · ${row.name}` : ''}
      </p>

      {row.issues.map((issue, i) => (
        <div key={i} className="mt-1.5">
          <p className={cn('text-[13px]', rejected ? 'text-danger' : 'text-[#9a6400]')}>
            {issue.message}
          </p>

          {issue.candidates && issue.candidates.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {issue.candidates.map((cand) => (
                <button
                  key={cand.id}
                  onClick={() => onResolve(cand.id)}
                  className={cn(
                    'min-h-9 rounded-full border px-3 text-[13px]',
                    override === cand.id
                      ? 'border-primary bg-primary text-white'
                      : 'border-border bg-surface text-ink hover:border-primary',
                  )}
                >
                  {override === cand.id && '✓ '}
                  {cand.path || cand.name}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </li>
  );
}
